import type { OrderDetails } from '@/entities/order/model'
import type { StateDefinition } from '@/entities/state/model'
import { useMantineTheme } from '@mantine/core'
import { useMemo } from 'react'
import { useStateConfigQuery, useStateDictionaryQuery } from '@/entities/state/model'
import { calculateAlertState } from './order.alertState'

/**
 * Hook that provides alert information for an order based on its state and elapsed time
 *
 * @param order - Order details
 * @returns Alert information including timer, animation state, severity, and formatted time
 */
export function useOrderAlert(order: OrderDetails) {
  const { data: stateConfig } = useStateConfigQuery()
  const { data: stateDictionary } = useStateDictionaryQuery()
  const { colors } = useMantineTheme()

  const defaultDisplayState = {
    orderDetailsTitle: order.status.toUpperCase(),
    color: colors.green[7],
    categoryTitle: order.status,
    stateDisplayName: order.status,
    orderDetailsSummary: `Order ${order.orderId}`,
  }

  const { alertState, displayState } = useMemo(() => {
    const stateDefinition: StateDefinition = stateDictionary?.[order.status] || {
      name: order.status,
      displayState: defaultDisplayState,
      eventTriggers: [],
      progressionLevel: 0,
      canAcknowledge: true,
      isActive: false,
      isError: false,
      shouldNotify: false,
      canForceTransition: false,
    }

    const alertState = calculateAlertState({
      order,
      targetState: stateDefinition,
      stateFeatures: stateConfig?.features,
      currentTime: new Date(),
    })

    return {
      alertState,
      displayState: stateDefinition.displayState || defaultDisplayState,
    }
  // eslint-disable-next-line react/exhaustive-deps
  }, [order, stateConfig, stateDictionary])

  return {
    alertState,
    displayState,
  }
}
